import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import { triggerNotificationRefresh } from '../../utils/notificationsApi';
import ActionDrawer from '../components/shared/ActionDrawer';
import { showServerError, showSuccess, showError, showShopifyCredentialsError } from '../../utils/toastHelper';

function ShopifyImportModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [importAll, setImportAll] = useState(false);
  const [hasShopifyCredentials, setHasShopifyCredentials] = useState(false);
  const [checkingCredentials, setCheckingCredentials] = useState(true);
  const [importState, setImportState] = useState(null);

  const [syncSettings, setSyncSettings] = useState({
    syncNameAndDescription: false,
    syncCategoryAndTags: false,
    syncImages: false,
    syncPriceAndStock: true // always true, but sent in payload just in case
  });

  useEffect(() => {
    if (!isOpen) return;

    const checkShopifyCredentials = async () => {
      try {
        setCheckingCredentials(true);
        const response = await apiRequest('/admin/settings');
        if (response.success && response.settings?.company) {
          const shopifyConfig = response.settings.company.shopifyConfig || {};
          const hasCredentials = !!shopifyConfig.shopUrl && (shopifyConfig.hasAccessToken || shopifyConfig.accessTokenMasked);
          setHasShopifyCredentials(hasCredentials);
          if (!hasCredentials) showShopifyCredentialsError('Lütfen şirket ayarlarından Shopify bilgilerini girin');
        } else {
          setHasShopifyCredentials(false);
        }
      } catch (error) {
        console.error('Failed to check Shopify credentials:', error);
        setHasShopifyCredentials(false);
      } finally {
        setCheckingCredentials(false);
      }
    };

    checkShopifyCredentials();
  }, [isOpen]);

  const hasTriggeredSuccess = useRef(false);

  useEffect(() => {
    let interval;
    if (isOpen) {
      hasTriggeredSuccess.current = false;
      const checkStatus = async () => {
        try {
          const res = await apiRequest('/products/import-status');
          if (res.success) {
            setImportState(res.state);
            // If the job finished, we can optionally trigger a UI refresh
            if (res.state?.status === 'COMPLETED' && !hasTriggeredSuccess.current) {
              hasTriggeredSuccess.current = true;
              if (onSuccess) onSuccess();
            }
          }
        } catch (e) {
          console.error('Failed to check import status', e);
        }
      };
      
      checkStatus();
      interval = setInterval(checkStatus, 3000);
    }
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isRunning = importState?.status === 'RUNNING';

  const handleCancel = async () => {
    try {
      setLoading(true);
      const res = await apiRequest('/products/import-cancel', { method: 'POST' });
      if (res.success) {
        showSuccess(res.message);
        // Will update UI on next poll
      } else {
        showError(res.message || 'İptal işlemi başarısız');
      }
    } catch (e) {
      showError('İptal işlemi sırasında hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!hasShopifyCredentials) {
      showShopifyCredentialsError('Lütfen şirket ayarlarından Shopify bilgilerini girin');
      return;
    }
    if (!searchTerm && !importAll) {
      showError('Lütfen bir arama terimi girin veya tümünü içe aktar seçeneğini işaretleyin');
      return;
    }

    try {
      setLoading(true);

      const response = await apiRequest('/products/import-from-shopify', {
        method: 'POST',
        body: JSON.stringify({
          searchTerm: importAll ? null : searchTerm,
          syncSettings
        })
      });

      if (response.success) {
        showSuccess('Import işlemi arka planda başlatıldı. Sonuç bildirimlerden takip edilebilir.');
        // Trigger notification refresh after a delay to allow the background job to complete
        setTimeout(() => triggerNotificationRefresh(), 5000);
        if (onSuccess) onSuccess();
        onClose();
      } else {
        showError(response.message || 'Import başlatılamadı');
      }
    } catch (error) {
      if (error?.apiError) {
        showServerError();
      } else {
        showError(error?.data?.message || error?.message || 'Hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const footer = (
    <div className="d-flex justify-content-end gap-2 w-100">
      <button type="button" className="btn btn-light" onClick={handleClose} disabled={loading}>İptal</button>
      
      {isRunning ? (
        <button type="button" className="btn btn-danger" disabled={loading} onClick={handleCancel}>
          {loading ? <><span className="spinner-border spinner-border-sm me-2" />Durduruluyor...</> : <><i className="fas fa-times-circle me-2" />İşlemi Durdur</>}
        </button>
      ) : (
        <button type="submit" className="btn btn-primary" disabled={loading || !hasShopifyCredentials || checkingCredentials || (!importAll && !searchTerm.trim())} onClick={handleSubmit}>
          {loading ? <><span className="spinner-border spinner-border-sm me-2" />Başlatılıyor...</> : <><i className="fas fa-download me-2" />İçe Aktar</>}
        </button>
      )}
    </div>
  );

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Shopify'dan Ürün İçe Aktar"
      width="600px"
      footer={footer}
    >
      <div className="shopify-import-form">
        {checkingCredentials ? (
          <div className="alert alert-info py-2 small text-center"><span className="spinner-border spinner-border-sm me-2" />Kontrol ediliyor...</div>
        ) : !hasShopifyCredentials && (
          <div className="alert alert-warning py-2 small"><i className="fas fa-exclamation-triangle me-2" />Shopify bağlantı bilgileri eksik.</div>
        )}

        <div className="alert alert-light py-2 small border mb-3">
          <i className="fas fa-info-circle me-2 text-primary" />
          Import arka planda çalışır. Tamamlandığında bildirim alacaksınız.
        </div>

        {isRunning && (
          <div className="alert alert-warning py-2 small mb-3">
            <span className="spinner-border spinner-border-sm me-2" />
            <strong>İçe aktarma devam ediyor...</strong> Lütfen bekleyin veya iptal edin.
          </div>
        )}

        <div className="card mb-3 border-light">
          <div className="card-header bg-light py-2">
            <h6 className="mb-0 small fw-bold"><i className="fas fa-sync me-2 text-primary"></i>Senkronizasyon Ayarları</h6>
          </div>
          <div className="card-body py-2 px-3">
            <p className="text-muted small mb-2" style={{ fontSize: '11px' }}>Zaten var olan ürünler güncellenirken üzerine yazılacak alanları seçin:</p>
            
            <div className="form-check form-switch mb-1">
              <input className="form-check-input" type="checkbox" checked={true} disabled />
              <label className="form-check-label small text-muted">Fiyat ve Stok (Zorunlu)</label>
            </div>
            
            <div className="form-check form-switch mb-1">
              <input className="form-check-input" type="checkbox" id="syncNameAndDesc" 
                checked={syncSettings.syncNameAndDescription} 
                onChange={(e) => setSyncSettings({...syncSettings, syncNameAndDescription: e.target.checked})} 
                disabled={loading || !hasShopifyCredentials || isRunning} />
              <label className="form-check-label small" htmlFor="syncNameAndDesc">İsim ve Açıklamalar</label>
            </div>
            
            <div className="form-check form-switch mb-1">
              <input className="form-check-input" type="checkbox" id="syncCategoryAndTags" 
                checked={syncSettings.syncCategoryAndTags} 
                onChange={(e) => setSyncSettings({...syncSettings, syncCategoryAndTags: e.target.checked})} 
                disabled={loading || !hasShopifyCredentials || isRunning} />
              <label className="form-check-label small" htmlFor="syncCategoryAndTags">Kategori ve Etiketler</label>
            </div>
            
            <div className="form-check form-switch mb-2">
              <input className="form-check-input" type="checkbox" id="syncImages" 
                checked={syncSettings.syncImages} 
                onChange={(e) => setSyncSettings({...syncSettings, syncImages: e.target.checked})} 
                disabled={loading || !hasShopifyCredentials || isRunning} />
              <label className="form-check-label small" htmlFor="syncImages">Görseller</label>
            </div>
          </div>
        </div>

        <div className="form-check mb-3">
          <input type="checkbox" className="form-check-input" id="importAll" checked={importAll} onChange={(e) => setImportAll(e.target.checked)} disabled={loading || !hasShopifyCredentials || isRunning} />
          <label className="form-check-label small" htmlFor="importAll">Tüm ürünleri getir</label>
        </div>

        {!importAll && (
          <div className="mb-3">
            <label className="form-label small fw-medium">Ürün Adı</label>
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-white"><i className="fas fa-search text-muted" /></span>
              <input type="text" className="form-control" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Ara..." disabled={loading || !hasShopifyCredentials || isRunning} />
            </div>
          </div>
        )}
      </div>
    </ActionDrawer>
  );
}

export default ShopifyImportModal;